import { formatUserFacingError } from '@/utils/userFacingError';

export const TERMINAL_PAYMENT_CANCELLED_REASON = 'Cancelled by staff';

export const TERMINAL_PAYMENT_DECLINED_RETRY_MESSAGE =
  'Card declined. Ask the customer to try another card.';

const RETRIABLE_TERMINAL_DECLINE_CODES = new Set([
  'card_declined',
  'insufficient_funds',
  'withdrawal_count_limit_exceeded',
  'generic_decline',
  'do_not_honor',
  'try_again_later',
  'processing_error',
  'authentication_required',
  'call_issuer',
  'incorrect_pin',
  'pin_try_exceeded',
]);

/** Staff-facing copy keyed by Stripe decline_code (preferred) or error code. */
const TERMINAL_DECLINE_MESSAGES: Record<string, string> = {
  call_issuer: 'Card declined. Ask the customer to call their card issuer, then try another card.',
  incorrect_pin: 'Incorrect PIN. Ask the customer to re-enter their PIN or try another card.',
  pin_try_exceeded: 'Too many PIN attempts. Ask the customer to use another card.',
  withdrawal_count_limit_exceeded:
    'Card withdrawal limit reached. Ask the customer to try another card.',
  insufficient_funds: 'Insufficient funds. Ask the customer to try another card.',
  do_not_honor: 'Card declined by the issuer. Ask the customer to try another card.',
  try_again_later: 'The issuer asked to try again later. Ask the customer to try another card.',
  authentication_required: 'The card needs authentication. Ask the customer to try another card.',
  generic_decline: TERMINAL_PAYMENT_DECLINED_RETRY_MESSAGE,
  card_declined: TERMINAL_PAYMENT_DECLINED_RETRY_MESSAGE,
};

export class TerminalPaymentCancelledError extends Error {
  constructor() {
    super(TERMINAL_PAYMENT_CANCELLED_REASON);
    this.name = 'TerminalPaymentCancelledError';
  }
}

export function isTerminalPaymentCancelledError(error: unknown): boolean {
  return error instanceof TerminalPaymentCancelledError;
}

function readTerminalErrorCode(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return '';
  }

  if ('code' in error && typeof (error as { code?: unknown }).code === 'string') {
    return (error as { code: string }).code.toLowerCase();
  }

  return '';
}

function readTerminalDeclineCode(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return '';
  }

  if (
    'decline_code' in error &&
    typeof (error as { decline_code?: unknown }).decline_code === 'string'
  ) {
    return (error as { decline_code: string }).decline_code.toLowerCase();
  }

  return '';
}

function readTerminalErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return '';
  }

  if ('message' in error && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }

  return '';
}

/** Stripe Terminal.js cancellation (staff cancel or reader abort), not a card decline. */
export function isTerminalUserCancellation(error: unknown): boolean {
  const code = readTerminalErrorCode(error);
  if (code === 'canceled' || code === 'cancelled') {
    return true;
  }

  const message = readTerminalErrorMessage(error).toLowerCase();
  return message.includes('cancel');
}

/** Card declined / insufficient funds — reuse the same PaymentIntent and collect again. */
export function isRetriableTerminalDecline(
  error: unknown,
  paymentIntent?: { status?: string },
): boolean {
  if (paymentIntent?.status === 'requires_payment_method') {
    return true;
  }

  const code = readTerminalErrorCode(error);
  if (RETRIABLE_TERMINAL_DECLINE_CODES.has(code)) {
    return true;
  }

  const message = readTerminalErrorMessage(error).toLowerCase();
  return (
    message.includes('declined') ||
    message.includes('insufficient') ||
    message.includes('try another card') ||
    message.includes('different card')
  );
}

/**
 * Builds a clear, staff-facing decline message. Prefers Stripe's `decline_code`
 * (e.g. `incorrect_pin`), then the error `code`, then Stripe's own message, then a
 * generic retry prompt.
 */
export function describeTerminalDecline(
  error: unknown,
  fallback: string = TERMINAL_PAYMENT_DECLINED_RETRY_MESSAGE,
): string {
  const declineCode = readTerminalDeclineCode(error);
  if (declineCode && TERMINAL_DECLINE_MESSAGES[declineCode]) {
    return TERMINAL_DECLINE_MESSAGES[declineCode];
  }

  const code = readTerminalErrorCode(error);
  if (code && TERMINAL_DECLINE_MESSAGES[code]) {
    return TERMINAL_DECLINE_MESSAGES[code];
  }

  return formatUserFacingError(error, fallback);
}

/** Manual-capture Terminal flow: processPayment succeeds when ready to capture. */
export function isSuccessfulTerminalPaymentIntent(paymentIntent: { status?: string }): boolean {
  return paymentIntent.status === 'requires_capture' || paymentIntent.status === 'succeeded';
}
